import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, MessageSquarePlus } from "lucide-react";
import { getReviews, addReview } from "@/lib/api";
import { Stars, StarInput } from "@/components/Stars";

export const Reviews = ({ slug }) => {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["reviews", slug], queryFn: () => getReviews(slug) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ author: "", rating: 5, comment: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author.trim()) { toast.error("Introdu numele"); return; }
    setSaving(true);
    try {
      await addReview(slug, form);
      toast.success("Recenzie adăugată. Mulțumim!");
      setForm({ author: "", rating: 5, comment: "" });
      setOpen(false);
      refetch();
    } catch {
      toast.error("Eroare la trimiterea recenziei");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return null;
  const { reviews = [], avg_rating = 0, count = 0 } = data || {};

  return (
    <section className="mt-14" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-black text-navy text-2xl md:text-3xl tracking-tight">Recenzii clienți</h2>
          <div className="flex items-center gap-2 mt-2">
            <Stars value={avg_rating} showValue />
            <span className="text-sm text-muted-foreground">· {count} recenzii</span>
          </div>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="bg-navy hover:bg-navy-700 text-white font-display font-bold px-5 py-2.5 rounded-xl inline-flex items-center gap-2 transition-colors"
          data-testid="write-review-toggle"
        >
          <MessageSquarePlus className="h-5 w-5" /> Scrie o recenzie
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="bg-slate-50 rounded-2xl p-6 mb-8 border-2 border-slate-100" data-testid="review-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-navy mb-1.5">Numele tău</label>
              <input className="inp" value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} data-testid="review-author" />
            </div>
            <div>
              <label className="block text-sm font-bold text-navy mb-1.5">Rating</label>
              <StarInput value={form.rating} onChange={(r) => setForm((f) => ({ ...f, rating: r }))} />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-bold text-navy mb-1.5">Părerea ta</label>
            <textarea className="inp" rows={3} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} data-testid="review-comment" />
          </div>
          <button type="submit" disabled={saving} className="mt-4 bg-eco hover:bg-green-700 text-white font-bold px-6 py-2.5 rounded-xl inline-flex items-center gap-2 disabled:opacity-60" data-testid="review-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Trimite recenzia
          </button>
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white border-2 border-slate-100 rounded-xl p-5" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-display font-bold text-navy">{r.author}</span>
              <Stars value={r.rating} size="h-3.5 w-3.5" />
            </div>
            <p className="text-sm text-slate-700 mt-2">{r.comment}</p>
            <p className="text-xs text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString("ro-RO")}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
